import { useEffect, useRef, useState } from 'react';
import { StatusChip, TimelineButton, LegalTimelineModal } from './legalShared';

const COLORS = ['#7c3aed', '#2563eb', '#0d9488', '#d97706', '#dc2626', '#059669', '#c026d3', '#0891b2', '#65a30d', '#e11d48'];

const STATUS_COLORS = {
    'Completed': '#059669',
    'In Progress': '#2563eb',
    'On Hold': '#d97706',
    'Pending': '#d97706',
    'Signed copy awaited': '#7c3aed',
    'Cancelled': '#dc2626',
    'NA': '#94a3b8',
};

function colorFor(label, index) {
    return STATUS_COLORS[label] || COLORS[index % COLORS.length];
}

// ── Hover tooltip — a small floating card positioned relative to whichever
// chart container is currently hovered, replacing the native SVG <title>.
function ChartTooltip({ point }) {
    if (!point) return null;
    return (
        <div className="legal-tooltip" style={{ left: point.x, top: point.y }}>
            <strong>{point.label}</strong>
            <span>{point.value} document{point.value === 1 ? '' : 's'}{point.percent != null ? ` · ${point.percent}%` : ''}</span>
        </div>
    );
}

function InsightCard({ icon, title, sub, children, className = '' }) {
    return (
        <div className={`legal-ic${className ? ` ${className}` : ''}`}>
            <div className="legal-ic-title">{icon} {title}</div>
            {sub && <div className="legal-ic-sub">{sub}</div>}
            {children}
        </div>
    );
}

function BarChart({ items, onBarClick }) {
    const [hover, setHover] = useState(null);
    // The SVG viewBox is sized to the container's actual pixel width (rather
    // than a fixed constant) so width:100% never has to squeeze/stretch the
    // chart to fit — bars always span the full card with no dead space.
    const [measuredWidth, setMeasuredWidth] = useState(700);
    const wrapRef = useRef(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const update = () => setMeasuredWidth(w => (el.clientWidth ? el.clientWidth : w));
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const total = items.reduce((s, i) => s + i.value, 0) || 1;
    const width = Math.max(300, measuredWidth), height = 190;
    const pad = { left: 28, right: 8, top: 14, bottom: 62 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const max = Math.max(...items.map(i => i.value), 1);
    const barW = items.length ? Math.min(24, innerW / items.length * .7) : 0;
    const step = items.length ? innerW / items.length : innerW;

    const showTooltip = (event, item) => {
        const rect = wrapRef.current.getBoundingClientRect();
        setHover({
            label: item.label, value: item.value,
            percent: (item.value / total * 100).toFixed(1),
            x: event.clientX - rect.left, y: event.clientY - rect.top,
        });
    };

    if (!items.length) return <div className="legal-chart-empty" ref={wrapRef}>No data available.</div>;

    return (
        <div className="legal-chart-wrap" ref={wrapRef}>
            <svg viewBox={`0 0 ${width} ${height}`} className="legal-bar-svg" role="img" aria-label="Bar chart">
                {[0, .25, .5, .75, 1].map(ratio => {
                    const y = pad.top + innerH * (1 - ratio);
                    return (
                        <g key={ratio}>
                            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#eef1f5" />
                            <text x={pad.left - 5} y={y + 3} textAnchor="end" fontSize="8" fill="#94a3b8">{Math.round(max * ratio)}</text>
                        </g>
                    );
                })}
                {items.map((item, index) => {
                    const x = pad.left + index * step + step / 2;
                    const barH = Math.max(1, item.value / max * innerH);
                    const bottom = pad.top + innerH;
                    const color = colorFor(item.label, index);
                    return (
                        <g
                            key={item.label}
                            className="legal-bar-g"
                            onMouseMove={e => showTooltip(e, item)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => onBarClick?.(item.label)}
                        >
                            <rect className="legal-bar-rect" x={x - barW / 2} y={bottom - barH} width={barW} height={barH} fill={color} rx="3" />
                            <text className="legal-bar-val" x={x} y={bottom - barH - 5} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#334155">{item.value}</text>
                            <text x={x} y={bottom + 11} textAnchor="end" fontSize="8" fill="#6b7280" transform={`rotate(-55 ${x} ${bottom + 11})`}>
                                {item.label.length > 14 ? `${item.label.slice(0, 13)}…` : item.label}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <ChartTooltip point={hover} />
        </div>
    );
}

function HorizontalRankingChart({ items, onBarClick }) {
    const total = items.reduce((sum, item) => sum + item.value, 0);
    const max = Math.max(...items.map(item => item.value), 1);
    if (!items.length) return <div className="legal-chart-empty">No data available.</div>;

    return (
        <div className="legal-ranking-chart">
            {items.map((item, index) => {
                const percent = total ? item.value / total * 100 : 0;
                return (
                    <button
                        type="button"
                        className="legal-ranking-row"
                        key={item.label}
                        onClick={() => onBarClick?.(item.label)}
                        title={`${item.label}: ${item.value} documents (${percent.toFixed(1)}%)`}
                    >
                        <span className="legal-ranking-position">{index + 1}</span>
                        <span className="legal-ranking-name">{item.label}</span>
                        <span className="legal-ranking-track">
                            <span
                                className="legal-ranking-fill"
                                style={{ width: `${item.value / max * 100}%`, background: colorFor(item.label, index) }}
                            />
                        </span>
                        <span className="legal-ranking-value">{item.value}</span>
                        <span className="legal-ranking-percent">{percent.toFixed(1)}%</span>
                    </button>
                );
            })}
        </div>
    );
}

// Classic "squarify" treemap layout (Bruls/Huizing/van Wijk) — packs every
// item into one screen at once, sized by value, so a high-cardinality field
// like Document Type (100+ distinct values) stays readable without paging,
// scrolling, or dropping the long tail the way a bar/bubble chart would.
function squarify(items, x0, y0, w0, h0) {
    const total = items.reduce((sum, item) => sum + item.value, 0) || 1;
    const scale = (w0 * h0) / total;
    const queue = items.map(item => ({ ...item, area: Math.max(item.value * scale, 0.01) }));

    const worst = (row, length) => {
        const sum = row.reduce((s, d) => s + d.area, 0);
        const max = Math.max(...row.map(d => d.area));
        const min = Math.min(...row.map(d => d.area));
        return Math.max((length * length * max) / (sum * sum), (sum * sum) / (length * length * min));
    };

    const result = [];
    let x = x0, y = y0, w = w0, h = h0;
    let remaining = queue;

    while (remaining.length) {
        const length = Math.min(w, h);
        let row = [remaining[0]];
        let i = 1;
        while (i < remaining.length) {
            const testRow = [...row, remaining[i]];
            if (worst(testRow, length) <= worst(row, length)) {
                row = testRow;
                i += 1;
            } else break;
        }

        const rowSum = row.reduce((s, d) => s + d.area, 0);
        if (w >= h) {
            const rowWidth = rowSum / h;
            const colX = x;
            let offsetY = y;
            row.forEach(item => {
                const itemHeight = item.area / rowWidth;
                result.push({ x: colX, y: offsetY, w: rowWidth, h: itemHeight, item });
                offsetY += itemHeight;
            });
            x += rowWidth;
            w -= rowWidth;
        } else {
            const rowHeight = rowSum / w;
            const rowY = y;
            let offsetX = x;
            row.forEach(item => {
                const itemWidth = item.area / rowHeight;
                result.push({ x: offsetX, y: rowY, w: itemWidth, h: rowHeight, item });
                offsetX += itemWidth;
            });
            y += rowHeight;
            h -= rowHeight;
        }
        remaining = remaining.slice(row.length);
    }
    return result;
}

function Treemap({ items, onCellClick, height = 400 }) {
    const [hover, setHover] = useState(null);
    const [measuredWidth, setMeasuredWidth] = useState(900);
    const wrapRef = useRef(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const update = () => setMeasuredWidth(width => (el.clientWidth || width));
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    if (!items.length) return <div className="legal-chart-empty">No data available.</div>;

    const width = Math.max(280, measuredWidth);
    const sorted = [...items].sort((a, b) => b.value - a.value);
    const cells = squarify(sorted, 0, 0, width, height);
    const total = sorted.reduce((sum, item) => sum + item.value, 0) || 1;

    const showTooltip = (event, item) => {
        const rect = wrapRef.current.getBoundingClientRect();
        setHover({
            label: item.label,
            value: item.value,
            percent: (item.value / total * 100).toFixed(1),
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        });
    };

    return (
        <div className="legal-treemap-wrap" ref={wrapRef} style={{ height }}>
            {cells.map(({ x, y, w, h, item }, index) => {
                const canLabel = w > 46 && h > 26;
                const canValue = w > 30 && h > 16;
                return (
                    <button
                        type="button"
                        key={item.label}
                        className="legal-treemap-cell"
                        style={{
                            left: x, top: y, width: Math.max(w - 1, 0), height: Math.max(h - 1, 0),
                            background: colorFor(item.label, index),
                        }}
                        onMouseMove={event => showTooltip(event, item)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => onCellClick?.(item.label)}
                        title={`${item.label}: ${item.value} document${item.value === 1 ? '' : 's'}`}
                    >
                        {canLabel && <span className="legal-treemap-label">{item.label}</span>}
                        {canValue && <span className="legal-treemap-value">{item.value}</span>}
                    </button>
                );
            })}
            <ChartTooltip point={hover} />
        </div>
    );
}

function Donut({ items, onSliceClick }) {
    const [hover, setHover] = useState(null);
    const wrapRef = useRef(null);
    const visible = items.filter(i => i.value > 0);
    if (!visible.length) return <div className="legal-chart-empty">No data available.</div>;

    const sum = visible.reduce((s, i) => s + i.value, 0) || 1;
    const center = 76, outer = 66, inner = 41;
    let angle = -Math.PI / 2;
    const arcs = visible.map((item, index) => {
        const sweep = item.value / sum * Math.PI * 2;
        const start = angle;
        angle += sweep;
        const point = (radius, radians) => [center + radius * Math.cos(radians), center + radius * Math.sin(radians)];
        const [x1, y1] = point(outer, start);
        const [x2, y2] = point(outer, angle);
        const [x3, y3] = point(inner, start);
        const [x4, y4] = point(inner, angle);
        return {
            item, color: colorFor(item.label, index),
            path: `M${x1} ${y1}A${outer} ${outer} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2} ${y2}L${x4} ${y4}A${inner} ${inner} 0 ${sweep > Math.PI ? 1 : 0} 0 ${x3} ${y3}Z`,
        };
    });

    const showTooltip = (event, item) => {
        const rect = wrapRef.current.getBoundingClientRect();
        setHover({
            label: item.label, value: item.value,
            percent: (item.value / sum * 100).toFixed(1),
            x: event.clientX - rect.left, y: event.clientY - rect.top,
        });
    };

    return (
        <div className="legal-donut-row" ref={wrapRef}>
            <div className="legal-donut-svg" style={{ position: 'relative' }}>
                <svg viewBox="0 0 152 152" aria-label="Distribution chart" style={{ width: '100%', display: 'block' }}>
                    {arcs.map(({ item, color, path }) => (
                        <path
                            key={item.label}
                            className="legal-arc"
                            d={path}
                            fill={color}
                            stroke="#fff"
                            strokeWidth="2"
                            onMouseMove={e => showTooltip(e, item)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => onSliceClick?.(item.label)}
                        />
                    ))}
                </svg>
                <div className="legal-donut-center">
                    <div className="legal-donut-center-val">{sum}</div>
                    <div className="legal-donut-center-lbl">Total</div>
                </div>
            </div>
            <div className="legal-legend">
                {arcs.map(({ item, color }) => (
                    <button type="button" className="legal-legend-row" key={item.label} onClick={() => onSliceClick?.(item.label)}>
                        <span className="legal-legend-dot" style={{ background: color }} />
                        <span className="legal-legend-lbl" title={item.label}>{item.label}</span>
                        <span className="legal-legend-val">{item.value}</span>
                    </button>
                ))}
            </div>
            <ChartTooltip point={hover} />
        </div>
    );
}

function StatusComparisonChart({ charts, onSegmentClick }) {
    const statuses = [...new Set(charts.flatMap(chart => chart.items.map(item => item.label)))];
    if (!statuses.length) return <div className="legal-chart-empty">No status data available.</div>;

    return (
        <div className="legal-status-comparison">
            <div className="legal-comparison-legend">
                {statuses.map((status, index) => (
                    <span key={status} className="legal-comparison-legend-item">
                        <span className="legal-legend-dot" style={{ background: colorFor(status, index) }} />
                        {status}
                    </span>
                ))}
            </div>
            <div className="legal-comparison-rows">
                {charts.map(chart => {
                    const total = chart.items.reduce((sum, item) => sum + item.value, 0);
                    return (
                        <div className="legal-comparison-row" key={chart.field}>
                            <div className="legal-comparison-label">{chart.title}</div>
                            <div className="legal-comparison-bar">
                                {chart.items.filter(item => item.value > 0).map(item => (
                                    <button
                                        type="button"
                                        key={item.label}
                                        className="legal-comparison-segment"
                                        style={{ width: `${item.value / total * 100}%`, background: colorFor(item.label, statuses.indexOf(item.label)) }}
                                        title={`${chart.title} — ${item.label}: ${item.value}`}
                                        onClick={() => onSegmentClick(chart.field, chart.title, item.label)}
                                    >
                                        {item.value}
                                    </button>
                                ))}
                                {!total && <span className="legal-comparison-empty">No data</span>}
                            </div>
                            <div className="legal-comparison-total">{total}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function LegalDrillModal({ title, sub, rows, onTimeline, onClose }) {
    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal" style={{ maxWidth: 860 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🔎</div>
                    <div>
                        <div className="legal-modal-title">{title}</div>
                        <div className="legal-modal-sub">{sub}</div>
                    </div>
                    <span className="legal-drill-count">{rows.length}</span>
                    <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 12 }}>✕</button>
                </div>
                <div className="legal-modal-body" style={{ padding: 0 }}>
                    {rows.length ? (
                        <div className="legal-drill-table-wrap">
                            <table className="legal-drill-table">
                                <thead>
                                    <tr>
                                        <th>Entity Name</th>
                                        <th>Document Category</th>
                                        <th>Document Sub-Category</th>
                                        <th>Requestor</th>
                                        <th>Country</th>
                                        <th>Received On</th>
                                        <th>Current Status</th>
                                        <th>Current Status Comments</th>
                                        <th>Legal Status</th>
                                        <th>Finance Status</th>
                                        <th>1Kosmos Signatory Status</th>
                                        <th>Partner Signatory Status</th>
                                        <th>Saved on Relativity</th>
                                        <th>Timeline</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => (
                                        <tr key={row.id || i}>
                                            <td><strong>{row.entity_name || '—'}</strong></td>
                                            <td>{row.document_category || '—'}</td>
                                            <td>{row.document_subcategory || '—'}</td>
                                            <td>{row.requestor_name || '—'}</td>
                                            <td>{row.country || '—'}</td>
                                            <td>{row.received_on || '—'}</td>
                                            <td><StatusChip value={row.current_status} /></td>
                                            <td>{row.current_status_comments || '—'}</td>
                                            <td><StatusChip value={row.legal_status} /></td>
                                            <td><StatusChip value={row.finance_status} /></td>
                                            <td><StatusChip value={row.kosmos_signatory_status} /></td>
                                            <td><StatusChip value={row.partner_signatory_status} /></td>
                                            <td><StatusChip value={row.saved_on_relativity} /></td>
                                            <td><TimelineButton onClick={() => onTimeline(row)} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="legal-drill-empty">No documents match this segment.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function LegalInsights({ data, searchQuery = '', canManageData, onStatusChanged }) {
    const [drill, setDrill] = useState(null);
    const [timelineRecord, setTimelineRecord] = useState(null);
    const records = data?.records || [];
    const requestorItems = data?.requestor_breakdown || [];
    const statusItems = data?.status_breakdown || [];
    const statusCharts = [
        { field: 'current_status', title: 'Current Status', sub: 'Overall parent status across the document register', items: statusItems },
        { field: 'legal_status', title: 'Legal Status', sub: 'Legal review progress', items: data?.legal_status_breakdown || [] },
        { field: 'finance_status', title: 'Finance Status', sub: 'Finance review progress', items: data?.finance_status_breakdown || [] },
        { field: 'kosmos_signatory_status', title: '1Kosmos Signatory Status', sub: 'Internal signature progress', items: data?.kosmos_signatory_status_breakdown || [] },
        { field: 'partner_signatory_status', title: 'Partner Signatory Status', sub: 'Partner signature progress', items: data?.partner_signatory_status_breakdown || [] },
        { field: 'saved_on_relativity', title: 'Saved on Relativity', sub: 'Relativity storage progress', items: data?.saved_on_relativity_breakdown || [] },
    ];
    const countryItems = data?.country_breakdown || [];
    const documentCategoryItems = data?.document_category_breakdown || [];
    const documentSubcategoryItems = data?.document_subcategory_breakdown || [];
    const total = data?.kpis?.total ?? 0;

    const hasSearch = Boolean(searchQuery.trim());

    if (!requestorItems.length && !statusItems.length && !hasSearch) {
        return <div className="pl-empty"><p>No records to analyze yet.</p></div>;
    }

    const openSegment = (field, chartLabel) => (label) => {
        const rows = records.filter(row => (row[field] || 'Unspecified') === label);
        setDrill({ title: `${chartLabel}: ${label}`, sub: `${rows.length} document${rows.length === 1 ? '' : 's'} in the current filter`, rows });
    };

    const topRequestor = requestorItems[0];
    const completed = data?.kpis?.completed ?? 0;
    const completionPct = total ? Math.round(completed / total * 100) : 0;

    return (
        <div className="legal-ov">
            <div className="legal-hero-row">
                <div className="legal-hero-tile legal-hero-slate">
                    <span className="legal-hero-icon">📄</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Total Documents</div>
                        <div className="legal-hero-val">{total}</div>
                        <div className="legal-hero-sub">in current filter</div>
                    </div>
                </div>
                <div className="legal-hero-tile legal-hero-violet">
                    <span className="legal-hero-icon">🏆</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Top Requestor</div>
                        <div className="legal-hero-val" title={topRequestor?.label}>{topRequestor?.label || '—'}</div>
                        <div className="legal-hero-sub">{topRequestor?.value ?? 0} documents</div>
                    </div>
                </div>
                <div className="legal-hero-tile legal-hero-green">
                    <span className="legal-hero-icon">✅</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Completion Rate</div>
                        <div className="legal-hero-val">{completionPct}%</div>
                        <div className="legal-hero-sub">{completed} of {total} completed</div>
                    </div>
                </div>
                <div className="legal-hero-tile legal-hero-cyan">
                    <span className="legal-hero-icon">🌍</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Countries</div>
                        <div className="legal-hero-val">{countryItems.length}</div>
                        <div className="legal-hero-sub">represented</div>
                    </div>
                </div>
            </div>

            {hasSearch && (
                <InsightCard
                    icon="🔎"
                    title={`Search Results: ${searchQuery.trim()}`}
                    sub={`${records.length} matching document${records.length === 1 ? '' : 's'}`}
                >
                    {records.length ? (
                        <div className="legal-drill-table-wrap">
                            <table className="legal-drill-table">
                                <thead>
                                    <tr>
                                        <th>Entity Name</th>
                                        <th>Document Category</th>
                                        <th>Document Sub-Category</th>
                                        <th>Requestor</th>
                                        <th>Country</th>
                                        <th>Received On</th>
                                        <th>Current Status</th>
                                        <th>Current Status Comments</th>
                                        <th>Legal Status</th>
                                        <th>Finance Status</th>
                                        <th>1Kosmos Signatory Status</th>
                                        <th>Partner Signatory Status</th>
                                        <th>Saved on Relativity</th>
                                        <th>Timeline</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((row, i) => (
                                        <tr key={row.id || i}>
                                            <td><strong>{row.entity_name || '—'}</strong></td>
                                            <td>{row.document_category || '—'}</td>
                                            <td>{row.document_subcategory || '—'}</td>
                                            <td>{row.requestor_name || '—'}</td>
                                            <td>{row.country || '—'}</td>
                                            <td>{row.received_on || '—'}</td>
                                            <td><StatusChip value={row.current_status} /></td>
                                            <td>{row.current_status_comments || '—'}</td>
                                            <td><StatusChip value={row.legal_status} /></td>
                                            <td><StatusChip value={row.finance_status} /></td>
                                            <td><StatusChip value={row.kosmos_signatory_status} /></td>
                                            <td><StatusChip value={row.partner_signatory_status} /></td>
                                            <td><StatusChip value={row.saved_on_relativity} /></td>
                                            <td><TimelineButton onClick={() => setTimelineRecord(row)} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="legal-drill-empty">No documents match “{searchQuery.trim()}”.</div>
                    )}
                </InsightCard>
            )}

            <InsightCard icon="📊" title="Requestor Volume" sub={`${requestorItems.length} requestors · hover a bar for detail, click to see its documents`}>
                <BarChart items={requestorItems} onBarClick={openSegment('requestor_name', 'Requestor')} />
            </InsightCard>

            <InsightCard icon="📊" title="Status Comparison" sub="Compare every workflow status; click a segment to see its documents">
                <StatusComparisonChart
                    charts={statusCharts}
                    onSegmentClick={(field, title, status) => openSegment(field, title)(status)}
                />
            </InsightCard>

            <div className="legal-ov-grid">
                {statusCharts.map(chart => (
                    <InsightCard key={chart.field} icon="🥧" title={chart.title} sub={chart.sub}>
                        <Donut items={chart.items} onSliceClick={openSegment(chart.field, chart.title)} />
                    </InsightCard>
                ))}
                <InsightCard className="legal-ic-full-row" icon="🥧" title="Country" sub="Where the counterparty entities are based">
                    <HorizontalRankingChart items={countryItems} onBarClick={openSegment('country', 'Country')} />
                </InsightCard>
            </div>

            <InsightCard icon="🌳" title="Document Category" sub={`All ${documentCategoryItems.length} categories · box size = document count · click a box to see its documents`}>
                <Treemap items={documentCategoryItems} onCellClick={openSegment('document_category', 'Document Category')} />
            </InsightCard>

            <InsightCard icon="🌳" title="Document Sub-Category" sub={`All ${documentSubcategoryItems.length} sub-categories · box size = document count · click a box to see its documents`}>
                <Treemap items={documentSubcategoryItems} onCellClick={openSegment('document_subcategory', 'Document Sub-Category')} />
            </InsightCard>

            {drill && <LegalDrillModal {...drill} onTimeline={setTimelineRecord} onClose={() => setDrill(null)} />}
            {timelineRecord && (
                <LegalTimelineModal
                    recordId={timelineRecord.id}
                    entityName={timelineRecord.entity_name}
                    canManageData={canManageData}
                    onClose={() => setTimelineRecord(null)}
                    onChanged={onStatusChanged}
                />
            )}
        </div>
    );
}

export { LegalInsights };
export default LegalInsights;
